import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PdfModel } from '../../../../../../../../typings/api/skymap/rest/v0/.common';
import { iiafe } from '../../../../../../utils/async';
import { formattedDateTime } from '../../../../../../utils/dateUtils';
import { Dxf } from '../../../../../../viewer/elements/dxf';
import { Glb } from '../../../../../../viewer/elements/glb';
import { useSharedView } from '../../../../../hooks/use-shared-view';
import { InfoBox } from '../../../../info-box/info-box';
import { LabelledContainer } from '../../../../labelled-container/labelled-container';
import { Select } from '../../../../select/select';
import { TextBox } from '../../../../text-box/text-box';
import { AssetTags } from '../../components/asset-tags';
import { AssetVolumes } from '../../components/asset-volume/asset-volumes';
import { OpacitySection } from '../../components/opacity-section';
import { TimelineSection } from '../../components/timeline-section';
import { FullWidth } from '../../styles/grid';
import { dateTimeOrDefault, numberOrDefault, stringOrDefault } from '../../utils/formatting';

type Props = {
  asset: Dxf | Glb;
  refreshAsset: () => void;
};

const CadBody = (props: Props) => {
  const { t } = useTranslation();
  const [selectedVersion, setSelectedVersion] = React.useState(props.asset.selectedVersion!);
  const { isSharedView } = useSharedView();
  const [mas, setMas] = React.useState(props.asset.MAS?.toString() ?? '');

  const isMasEditable = useMemo(() => {
    if (props.asset instanceof Glb) {
      return false;
    }

    return (
      !isSharedView &&
      props.asset.loadingStatus !== 'loading' &&
      props.asset.visible &&
      props.asset.masVisible &&
      props.asset.masEditable
    );
  }, [props.asset, isSharedView]);

  return (
    <>
      <FullWidth>
        <Select
          options={(props.asset.versions ?? []).map((x, i) => ({
            id: x.id,
            name: `v${props.asset.versions!.length - i} - ${formattedDateTime(x.createdAt)}`,
          }))}
          value={selectedVersion.id}
          onChange={(e) => {
            const selectedVersion = props.asset.versions?.find((x) => x.id === e.target.value)!;
            setSelectedVersion(selectedVersion);

            iiafe(async () => {
              props.asset.selectedVersion = selectedVersion;
              await props.asset.onChange();
            });
          }}
        />
      </FullWidth>

      <PdfStatus fileVersionId={selectedVersion.id} pdfs={(props.asset as Glb).pdfs} />

      {props.asset instanceof Glb && props.asset.isConverting() && (
        <FullWidth>
          <InfoBox color={'yellow'} leftIcon={{ icon: 'spinner' }} leftIconSpin={true}>
            {t('cad.status.runningInfo', { ns: 'skyviewAssetMenu' })}
          </InfoBox>
        </FullWidth>
      )}

      {/* All versions of a drawing are converted when it is added, so one that can't be loaded
          failed to convert, see AssetManager.convertDrawingVersions. */}
      {props.asset instanceof Glb &&
        props.asset.isDrawing() &&
        props.asset.loadingStatus === 'error' && (
          <FullWidth>
            <InfoBox color={'red'} leftIcon={{ icon: 'warning' }}>
              {t('cad.status.drawingNotConvertedInfo', { ns: 'skyviewAssetMenu' })}
            </InfoBox>
          </FullWidth>
        )}

      <FullWidth apply={'masVisible' in props.asset && !props.asset.masVisible}>
        <LabelledContainer text={t('cad.section.scale', { ns: 'skyviewAssetMenu' })}>
          {stringOrDefault(props.asset instanceof Dxf ? props.asset.dxfUnitName : undefined)}
        </LabelledContainer>
      </FullWidth>

      {'masVisible' in props.asset && props.asset.masVisible && (
        <LabelledContainer text={t('cad.section.mas', { ns: 'skyviewAssetMenu' })}>
          {isMasEditable ? (
            <TextBox
              errorMessage={isNaN(parseFloat(mas)) ? '' : undefined}
              step={0.01}
              type="number"
              value={mas}
              onChange={(e) => {
                iiafe(async () => {
                  // Accept all input MAS values. Valid or not.
                  setMas(e.target.value);

                  const masFloat = parseFloat(e.target.value);
                  if (!isNaN(masFloat)) {
                    props.asset.MAS = masFloat;
                    await props.asset.onChange();
                  }
                });
              }}
            />
          ) : (
            <>{numberOrDefault(props.asset.MAS)}</>
          )}
        </LabelledContainer>
      )}

      <LabelledContainer text={t('cad.section.revision', { ns: 'skyviewAssetMenu' })}>
        {stringOrDefault(props.asset.revision)}
      </LabelledContainer>

      <LabelledContainer text={t('cad.section.revisionDate', { ns: 'skyviewAssetMenu' })}>
        {dateTimeOrDefault(props.asset.revisionDate)}
      </LabelledContainer>

      <LabelledContainer text={t('cad.section.uploadedBy', { ns: 'skyviewAssetMenu' })}>
        {stringOrDefault(props.asset.uploadedBy)}
      </LabelledContainer>

      <LabelledContainer text={t('cad.section.uploadedDate', { ns: 'skyviewAssetMenu' })}>
        {dateTimeOrDefault(props.asset.uploadDate)}
      </LabelledContainer>

      <FullWidth>
        <LabelledContainer text={t('cad.section.description', { ns: 'skyviewAssetMenu' })}>
          {stringOrDefault(props.asset.description)}
        </LabelledContainer>
      </FullWidth>

      <TimelineSection asset={props.asset} />

      {!isSharedView && props.asset.assetId && (
        <FullWidth>
          <LabelledContainer text={t('fileManager.actionBar.tags', { ns: 'components' })}>
            <AssetTags
              initialTagsIds={props.asset.assetTagIds}
              refreshAsset={props.refreshAsset}
              target={{ kind: 'dynamicAsset', id: props.asset.assetId, name: props.asset.name }}
            />
          </LabelledContainer>
        </FullWidth>
      )}
      <OpacitySection asset={props.asset} />

      {(props.asset instanceof Dxf || props.asset instanceof Glb) && (
        <FullWidth>
          <AssetVolumes asset={props.asset} />
        </FullWidth>
      )}
    </>
  );
};

const PdfStatus = (props: { pdfs?: PdfModel[]; fileVersionId: string }) => {
  const { t } = useTranslation();
  const pdf = useMemo<PdfModel | undefined>(() => {
    return props.pdfs?.find((x) => x.fileVersionId === props.fileVersionId);
  }, [props.pdfs, props.fileVersionId]);

  if (pdf?.status === 'RUNNING') {
    return (
      <FullWidth>
        <InfoBox color={'yellow'} leftIcon={{ icon: 'spinner' }} leftIconSpin={true}>
          {t('cad.status.runningInfo', { ns: 'skyviewAssetMenu' })}
        </InfoBox>
      </FullWidth>
    );
  }

  if (pdf?.status === 'FAILED') {
    return (
      <FullWidth>
        <InfoBox color={'red'} leftIcon={{ icon: 'warning' }}>
          {t('cad.status.failedInfo', { ns: 'skyviewAssetMenu' })}
        </InfoBox>
      </FullWidth>
    );
  }

  return null;
};

export { CadBody, Props as CadBodyProps };
